/* global axios */
import ApiClient from './ApiClient';
import { mockCustomAnalytics } from './customAnalyticsMock';

/**
 * Custom Analytics client for the custom-metrics backend (singabot). It is reached only through
 * the Rails proxy `/api/v1/accounts/:id/proxy/knowledge/*`, which forwards the session headers,
 * query string and body. Administrators only (the backend answers 403 `admin_required` to agents).
 *
 *   GET    charts?tz=&inbox_id=        every chart with its data (one call per page load)
 *   POST   charts?tz=&inbox_id=        add a chart, returns it with data
 *   PATCH  charts/:id?tz=&inbox_id=    change a chart (any subset, `{}` = recompute)
 *   DELETE charts/:id
 *   PUT    charts/layout               { items: [{ id, x, y, w, h }] } changed charts only
 *   GET    keys                        { keys: string[], truncated }
 *   GET    values?key=&preset=...      full value list for the value filter
 *   POST   query                       data for an unsaved chart (Add chart preview)
 *
 * `view` is `{ tz, inbox_id }`: the browser time zone and the page's global inbox filter.
 *
 * The mock (localStorage-backed, same shapes) is off by default; switch it on for local work
 * with `localStorage.customAnalyticsMock = 'on'`.
 */
export const isMockEnabled = () => {
  try {
    return window.localStorage.getItem('customAnalyticsMock') === 'on';
  } catch (e) {
    return false;
  }
};

const viewParams = ({ tz, inbox_id: inboxId } = {}) => {
  const params = {};
  if (tz) params.tz = tz;
  if (inboxId) params.inbox_id = inboxId;
  return params;
};

// `inbox_id=1&inbox_id=2`, the repeated-parameter form GET /values expects.
const valuesQuery = ({
  key,
  time_range: range = {},
  inbox_ids: inboxIds = [],
  tz,
}) => {
  const query = new URLSearchParams();
  query.append('key', key);
  query.append('preset', range.preset || 'last_year');
  if (range.from) query.append('from', range.from);
  if (range.to) query.append('to', range.to);
  if (tz) query.append('tz', tz);
  inboxIds.forEach(id => query.append('inbox_id', id));
  return query.toString();
};

class CustomAnalyticsAPI extends ApiClient {
  constructor() {
    super('proxy/knowledge', { accountScoped: true });
  }

  // Not `baseUrl`: ApiClient's `url` getter calls `this.baseUrl()`, so overriding it with
  // something that reads `this.url` recurses until the stack overflows.
  metricsUrl() {
    return `${this.url}/custom-metrics/accounts/${this.accountIdFromRoute}`;
  }

  getCharts(view) {
    if (isMockEnabled()) {
      return mockCustomAnalytics.getCharts(this.accountIdFromRoute, view);
    }
    return axios.get(`${this.metricsUrl()}/charts`, {
      params: viewParams(view),
    });
  }

  getKeys() {
    if (isMockEnabled()) return mockCustomAnalytics.getKeys();
    return axios.get(`${this.metricsUrl()}/keys`);
  }

  getValues(params) {
    if (isMockEnabled()) {
      const { time_range: range = {} } = params;
      return mockCustomAnalytics.getValues(this.accountIdFromRoute, {
        key: params.key,
        preset: range.preset,
        from: range.from,
        to: range.to,
      });
    }
    return axios.get(`${this.metricsUrl()}/values?${valuesQuery(params)}`);
  }

  query(body) {
    if (isMockEnabled()) {
      return mockCustomAnalytics.query(this.accountIdFromRoute, body);
    }
    return axios.post(`${this.metricsUrl()}/query`, body);
  }

  createChart(body, view) {
    if (isMockEnabled()) {
      return mockCustomAnalytics.createChart(
        this.accountIdFromRoute,
        body,
        view
      );
    }
    return axios.post(`${this.metricsUrl()}/charts`, body, {
      params: viewParams(view),
    });
  }

  updateChart(id, body, view) {
    if (isMockEnabled()) {
      return mockCustomAnalytics.updateChart(
        this.accountIdFromRoute,
        id,
        body,
        view
      );
    }
    return axios.patch(`${this.metricsUrl()}/charts/${id}`, body, {
      params: viewParams(view),
    });
  }

  deleteChart(id) {
    if (isMockEnabled()) {
      return mockCustomAnalytics.deleteChart(this.accountIdFromRoute, id);
    }
    return axios.delete(`${this.metricsUrl()}/charts/${id}`);
  }

  saveLayout(items) {
    if (isMockEnabled()) {
      return mockCustomAnalytics.saveLayout(this.accountIdFromRoute, { items });
    }
    return axios.put(`${this.metricsUrl()}/charts/layout`, { items });
  }
}

export default new CustomAnalyticsAPI();
